import { useEffect, useMemo, useState } from 'react'
import { TeacherPriorityCard } from './TeacherPriorityCard'
import { formatDateShort, toLocalISOString } from '@/lib/date-utils'
import {
  brockenSeriesKey,
  cutLabel,
  weekdayOf,
  type BrockenUnit,
  type WeekRankMode,
} from '@/utils/brockenUnits'
import type { TeacherAvailability } from '@/types/teacher-request'
import './StundenplanSchnitt.css'

const WD_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const WD_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
const WD_PLURAL = ['Sonntage', 'Montage', 'Dienstage', 'Mittwoche', 'Donnerstage', 'Freitage', 'Samstage']
const PALETTE = ['#4f46e5', '#0d9488', '#d97706', '#7c3aed', '#0369a1', '#be123c']

export interface StundenplanBlock {
  id: string
  date?: string
  timeSlotIds: string[]
  blockName: string | null
  entries: { teacherId: string; rank: number; teacherName: string; teacherCode?: string; rating: number }[]
}

interface Props {
  units: BrockenUnit[]
  coverageBlocks: StundenplanBlock[]
  allTeachers: TeacherAvailability[]
  availabilityDays?: Record<string, { teachers?: TeacherAvailability[] }>
  selectedDate: string
  onToggleTeacher: (teacher: TeacherAvailability, seriesKey: string) => void
  weekRankMode?: WeekRankMode
}

type DayGroup = {
  dates: string[]
  units: BrockenUnit[]
}

function addDays(iso: string, n: number): string {
  const parts = iso.split('-').map(Number)
  const y = parts[0] ?? 0
  const mo = parts[1] ?? 1
  const d = parts[2] ?? 1
  return toLocalISOString(new Date(y, mo - 1, d + n))
}

function mondayOf(iso: string): string {
  const wd = weekdayOf(iso)
  const back = wd === 0 ? 6 : wd - 1
  return addDays(iso, -back)
}

function pieceColor(key: string): string {
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 33 + key.charCodeAt(i)) >>> 0
  return PALETTE[h % PALETTE.length] ?? '#4f46e5'
}

function hhmm(t?: string): string {
  return (t || '').slice(0, 5)
}

function compactDates(list: string[]): string {
  return list.map(formatDateShort).join(' · ')
}

function uniqueTeachers(pool: TeacherAvailability[]): TeacherAvailability[] {
  const seen = new Set<string>()
  const out: TeacherAvailability[] = []
  for (const t of pool) {
    if (seen.has(t.id)) continue
    seen.add(t.id)
    out.push(t)
  }
  return out
}

function buildWeeks(dates: string[], weekend: boolean): string[][] {
  const first = dates[0]
  const last = dates[dates.length - 1]
  if (!first || !last) return []
  const start = mondayOf(first)
  const weeks: string[][] = []
  let cursor = start
  while (cursor <= last) {
    const week = Array.from({ length: 7 }, (_, i) => addDays(cursor, i))
    weeks.push(weekend ? week : week.slice(0, 5))
    cursor = addDays(cursor, 7)
    if (weeks.length > 24) break
  }
  return weeks
}

function groupsForWeekday(
  wd: number,
  dates: string[],
  byDate: Map<string, BrockenUnit[]>,
): DayGroup[] {
  const map = new Map<string, string[]>()
  for (const date of dates.filter(d => weekdayOf(d) === wd)) {
    const dayUnits = byDate.get(date) || []
    const key = dayUnits.map(u => u.fingerprint).join('|')
    const list = map.get(key) || []
    list.push(date)
    map.set(key, list)
  }
  return [...map.values()].map(groupDates => ({
    dates: groupDates,
    units: byDate.get(groupDates[0] || '') || [],
  }))
}

export function StundenplanSchnitt({
  units,
  coverageBlocks,
  allTeachers,
  availabilityDays,
  selectedDate,
  onToggleTeacher,
  weekRankMode = 'repeat',
}: Props) {
  const dates = useMemo(() => [...new Set(units.map(u => u.date))].sort(), [units])
  const byDate = useMemo(() => {
    const map = new Map<string, BrockenUnit[]>()
    for (const u of units) {
      const list = map.get(u.date) || []
      list.push(u)
      map.set(u.date, list)
    }
    return map
  }, [units])
  const weekdaysPresent = useMemo(() => [...new Set(units.map(u => u.weekday))].sort((a, b) => {
    const na = a === 0 ? 7 : a
    const nb = b === 0 ? 7 : b
    return na - nb
  }), [units])
  const weekend = dates.some(d => weekdayOf(d) === 0 || weekdayOf(d) === 6)
  const weeks = useMemo(() => buildWeeks(dates, weekend), [dates, weekend])
  const colWds = weekend ? [1, 2, 3, 4, 5, 6, 0] : [1, 2, 3, 4, 5]
  const lastDate = dates[dates.length - 1]

  const [wdFilter, setWdFilter] = useState<number | 'all'>('all')
  const [focusDate, setFocusDate] = useState(dates[0] || selectedDate)
  const [liveKey, setLiveKey] = useState('')

  useEffect(() => {
    if (dates.length === 0) return
    const fallback = dates[0]
    if (!fallback) return
    setFocusDate(prev => (dates.includes(prev) ? prev : fallback))
  }, [dates])

  const focusUnits = byDate.get(focusDate) || []
  const liveUnit = focusUnits.find(u => brockenSeriesKey(u, weekRankMode) === liveKey) || focusUnits[0]
  const seriesKey = liveUnit ? brockenSeriesKey(liveUnit, weekRankMode) : ''

  useEffect(() => {
    if (!liveUnit) return
    const next = brockenSeriesKey(liveUnit, weekRankMode)
    if (next !== liveKey) setLiveKey(next)
  }, [liveUnit, liveKey, weekRankMode])

  const seriesUnits = useMemo(
    () => units.filter(u => brockenSeriesKey(u, weekRankMode) === seriesKey),
    [units, seriesKey, weekRankMode],
  )
  const seriesDates = useMemo(() => new Set(seriesUnits.map(u => u.date)), [seriesUnits])
  const echoDates = useMemo(() => {
    const s = new Set(seriesDates)
    s.delete(focusDate)
    return s
  }, [seriesDates, focusDate])

  const pick = (date: string, key?: string) => {
    setFocusDate(date)
    const dayUnits = byDate.get(date) || []
    const match = key ? dayUnits.find(u => brockenSeriesKey(u, weekRankMode) === key) : dayUnits.find(u => brockenSeriesKey(u, weekRankMode) === liveKey)
    const chosen = match || dayUnits[0]
    if (chosen) setLiveKey(brockenSeriesKey(chosen, weekRankMode))
  }

  const setFilter = (wd: number | 'all') => {
    setWdFilter(wd)
    if (wd === 'all') return
    const wdDates = dates.filter(d => weekdayOf(d) === wd)
    const first = wdDates[0]
    if (first && weekdayOf(focusDate) !== wd) pick(first)
  }

  const findBlock = (unit: BrockenUnit) =>
    coverageBlocks.find(b => b.id === `block-brocken-${unit.date}-${unit.fingerprint}`)
    || coverageBlocks.find(b => b.date === unit.date && [...b.timeSlotIds].sort().join(',') === unit.fingerprint)

  const sampleBlock = liveUnit ? findBlock(liveUnit) : undefined
  const slotIds = liveUnit?.timeSlotIds ?? []

  const rankedTeachers = useMemo(() => {
    if (!liveUnit) return []
    const slots = liveUnit.timeSlotIds
    const pool = uniqueTeachers(
      allTeachers.length ? allTeachers : (availabilityDays?.[focusDate]?.teachers || []),
    )
    const onDate = (t: TeacherAvailability, date: string) =>
      availabilityDays?.[date]?.teachers?.find(x => x.id === t.id) || t
    const free = (t: TeacherAvailability, date: string) => {
      const rec = onDate(t, date)
      return slots.every(id => (rec.available_slot_ids || []).includes(id))
    }
    return pool
      .map(t => ({
        teacher: onDate(t, focusDate),
        ok: seriesUnits.some(u => free(t, u.date)) || free(t, focusDate),
      }))
      .sort((a, b) => Number(b.ok) - Number(a.ok))
  }, [allTeachers, availabilityDays, focusDate, liveUnit, seriesUnits])

  if (units.length === 0) {
    return (
      <div className="p-6 bg-white rounded-lg shadow text-center">
        <p className="text-gray-700 font-medium">Keine Stücke</p>
        <p className="text-gray-500 text-sm mt-2">
          Doppelstunden bleiben zusammen. Pause oder Fachwechsel wird zum Schnitt.
        </p>
      </div>
    )
  }

  const otherSeriesDates = [...echoDates].sort()
  const sameWdMissing = dates.filter(d => weekdayOf(d) === weekdayOf(focusDate) && !seriesDates.has(d))
  const lessonCount = liveUnit
    ? (focusUnits.reduce((n, u) => n + u.lessons.length, 0))
    : 0
  const rankedCount = sampleBlock?.entries.length || 0
  const weekCols = `4.5rem repeat(${colWds.length}, minmax(0, 1fr))`

  const isTrailingEmpty = (date: string) => {
    if (!lastDate || date <= lastDate) return false
    return dates.some(d => weekdayOf(d) === weekdayOf(date))
  }

  const trailingEmpty = (wd: number) =>
    weeks.flat().filter(d => weekdayOf(d) === wd && !byDate.has(d) && isTrailingEmpty(d))

  const renderMini = (dayUnits: BrockenUnit[], date: string, groupDates?: string[]) => {
    const inFocus = groupDates ? groupDates.includes(focusDate) : date === focusDate
    const inEcho = groupDates ? groupDates.some(d => echoDates.has(d)) && !inFocus : echoDates.has(date)
    return (
      <div className="sps-mini">
        {dayUnits.map(u => {
          const key = brockenSeriesKey(u, weekRankMode)
          const color = pieceColor(key)
          const live = inFocus && key === seriesKey
          const echo = inEcho && key === seriesKey
          const target = groupDates?.includes(focusDate) ? focusDate : date
          return (
            <span
              key={key}
              className={live ? 'is-live' : echo ? 'is-echo' : ''}
              style={{ flex: u.lessons.length > 1 ? 2 : 1, background: color, color }}
              title={u.shortLabel}
              onClick={e => { e.stopPropagation(); pick(target, key) }}
            />
          )
        })}
      </div>
    )
  }

  const renderEmpty = (date: string) => (
    <div key={date} className="sps-cell is-empty">
      <div className="flex items-center justify-between gap-1 mb-1">
        <div className="text-xs text-slate-400">{formatDateShort(date)}</div>
        {isTrailingEmpty(date) ? <span className="sps-back">wieder da</span> : null}
      </div>
      {isTrailingEmpty(date) ? (
        <div className="text-[10px] text-slate-400 leading-tight">Lehrkraft unterrichtet wieder</div>
      ) : null}
    </div>
  )

  const renderCell = (date: string) => {
    const dayUnits = byDate.get(date)
    if (!dayUnits) return renderEmpty(date)
    const isFocus = date === focusDate
    const isEcho = echoDates.has(date)
    const cls = `sps-cell${isFocus ? ' is-focus' : isEcho ? ' is-echo' : ''}`
    return (
      <button
        key={date}
        type="button"
        className={cls}
        onClick={() => pick(date)}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold">{formatDateShort(date)}</span>
          <span className="text-[10px] text-slate-400">{dayUnits.length} Stücke</span>
        </div>
        {renderMini(dayUnits, date)}
      </button>
    )
  }

  const renderSeriesCell = (group: DayGroup) => {
    const sample = group.dates[0]
    if (!sample) return null
    const inFocus = group.dates.includes(focusDate)
    const inEcho = group.dates.some(d => echoDates.has(d)) && !inFocus
    const cls = `sps-cell${inFocus ? ' is-focus' : inEcho ? ' is-echo' : ''}`
    const wd = weekdayOf(sample)
    const title = group.dates.length === 1
      ? `${WD_LONG[wd]} ${formatDateShort(sample)}`
      : (WD_PLURAL[wd] || WD_LONG[wd])
    return (
      <button
        key={group.dates.join('|')}
        type="button"
        className={cls}
        onClick={() => pick(inFocus ? focusDate : sample)}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold">{title}</span>
          <span className="text-[10px] text-slate-400">{group.units.length} Stücke</span>
        </div>
        <div className="sps-date-line">{compactDates(group.dates)}</div>
        {renderMini(group.units, sample, group.dates)}
      </button>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-2">Alle Tage</div>
          <button
            type="button"
            className={`sps-wd${wdFilter === 'all' ? ' on' : ''}`}
            onClick={() => setFilter('all')}
          >
            Alle
          </button>
          {weekdaysPresent.map(wd => {
            const wdDates = dates.filter(d => weekdayOf(d) === wd)
            const on = wdFilter === wd
            const missing = trailingEmpty(wd)
            const sub = missing.length
              ? `${compactDates(wdDates)} · ${formatDateShort(missing[0] || '')} fehlt`
              : compactDates(wdDates)
            return (
              <button
                key={wd}
                type="button"
                className={`sps-wd${on ? ' on wide' : ''}`}
                onClick={() => setFilter(wd)}
              >
                {WD_SHORT[wd]}
                {on ? <small>{sub}</small> : null}
              </button>
            )
          })}
          <span className="ml-auto text-[11px] text-slate-500">
            {wdFilter === 'all'
              ? `${dates.length} Tage · ${weeks.length} Woche${weeks.length === 1 ? '' : 'n'}`
              : `${dates.filter(d => weekdayOf(d) === wdFilter).length} ${WD_PLURAL[wdFilter]} im Button`}
          </span>
        </div>
        {wdFilter === 'all' ? (
          <>
            <div className="grid gap-2 mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 px-1" style={{ gridTemplateColumns: weekCols }}>
              <div />
              {colWds.map(wd => <div key={wd}>{WD_SHORT[wd]}</div>)}
            </div>
            <div className="space-y-2">
              {weeks.map((week, i) => {
                const start = week[0]
                const end = week[week.length - 1]
                if (!start || !end) return null
                return (
                  <div key={start} className="grid gap-2" style={{ gridTemplateColumns: weekCols }}>
                    <div className="text-[11px] font-semibold text-slate-700 leading-tight pt-2">
                      Woche {i + 1}
                      <span className="block font-medium text-slate-400">{formatDateShort(start)}–{formatDateShort(end)}</span>
                    </div>
                    {week.map(renderCell)}
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {groupsForWeekday(wdFilter, dates, byDate).map(renderSeriesCell)}
            {trailingEmpty(wdFilter).map(renderEmpty)}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-3">
          {wdFilter === 'all'
            ? 'Alle Wochen im Bild. Nachleuchten nur auf dem gleichen Wochentag mit dem gleichen Schnitt.'
            : `Ein Button pro Schnittmuster. Alle ${WD_PLURAL[wdFilter]} stehen als Daten darin.`}
        </p>
      </div>

      <div className="bg-white border rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              {wdFilter === 'all'
                ? `${WD_LONG[weekdayOf(focusDate)]} ${formatDateShort(focusDate)} · Fokus`
                : `${WD_PLURAL[weekdayOf(focusDate)]} · Fokus`}
            </div>
            <div className="font-semibold">{lessonCount} Lektionen · {focusUnits.length} Stücke</div>
            <p className="text-xs text-slate-500">
              {liveUnit ? `Gewählt: ${liveUnit.shortLabel}. Andere Stücke schmal, Schnitte bleiben.` : ''}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-full">
              Serie · {seriesDates.size} {WD_PLURAL[weekdayOf(focusDate)]}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              {sameWdMissing.length
                ? `${sameWdMissing.map(formatDateShort).join(', ')} anderes Stück — eigene Serie`
                : (otherSeriesDates.length ? compactDates([...seriesDates].sort()) : 'Einziger Tag dieser Form')}
            </div>
          </div>
        </div>
        <div className="sps-row">
          {focusUnits.map((u, i) => {
            const key = brockenSeriesKey(u, weekRankMode)
            const live = key === seriesKey
            const color = pieceColor(key)
            const glue = u.lessons.length > 1
            const cut = u.cutAfter
            const prevCut = i > 0 ? (focusUnits[i - 1]?.cutAfter ?? null) : null
            return (
              <span key={u.date + u.fingerprint} className="contents">
                <button
                  type="button"
                  className={`sps-piece${live ? ' is-live' : ''}`}
                  style={{ background: color }}
                  aria-pressed={live}
                  onClick={() => pick(focusDate, key)}
                >
                  <span className="sps-glow" />
                  <span className="sps-shimmer" />
                  <span className="sps-ring" />
                  {glue ? <span className="sps-glue" /> : null}
                  <span className="sps-tag">gewählt</span>
                  <div className="sps-compact">
                    <div className="text-[10px] opacity-80">{hhmm(u.lessons[0]?.startTime)}</div>
                    <div className="text-xs font-semibold leading-tight">{u.shortLabel}</div>
                  </div>
                  <div className="sps-live">
                    <div className={`grid ${glue ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {u.lessons.map((l, idx) => (
                        <div key={l.entryId} className={`px-3 py-2.5 ${idx ? 'bg-black/10' : ''}`}>
                          <div className="text-[10px] opacity-80">{hhmm(l.startTime)}</div>
                          <div className="text-sm font-semibold">
                            {[l.subjectName, l.klassName].filter(Boolean).join(' ') || 'Lektion'}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-3 pb-2 text-[10px] opacity-80">
                      {glue
                        ? 'Zusammenhängend · bleibt zusammen'
                        : (prevCut ? cutLabel(prevCut) : 'Eigenes Stück')}
                    </div>
                  </div>
                </button>
                {cut && i < focusUnits.length - 1 ? (
                  <div className="sps-cut">
                    <svg className="w-3.5 h-3.5 mb-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="6" cy="6" r="3" />
                      <circle cx="6" cy="18" r="3" />
                      <path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
                    </svg>
                    {cutLabel(cut).replace(' ', '\n')}
                  </div>
                ) : null}
              </span>
            )
          })}
        </div>
      </div>

      <div className="sps-panel bg-white border rounded-xl p-4" key={seriesKey + focusDate}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              {WD_LONG[weekdayOf(focusDate)]}-Serie · {seriesDates.size} Tag{seriesDates.size === 1 ? '' : 'e'}
            </div>
            <h3 className="font-semibold">{liveUnit?.shortLabel || 'Stück'}</h3>
            <p className="text-sm text-slate-500">
              {seriesDates.size > 1
                ? `Rang gilt für ${compactDates([...seriesDates].sort())}. Jeder Tag bleibt einzeln annehmbar.`
                : `Nur dieser ${WD_LONG[weekdayOf(focusDate)]} hat dieses Stück in dieser Form.`}
            </p>
          </div>
          <span className="text-xs text-slate-400">
            {seriesDates.size > 1
              ? (rankedCount
                ? `${rankedCount} gesetzt · nicht ${rankedCount * seriesDates.size}×`
                : `kein Rang · nicht ${seriesDates.size} Kopien`)
              : `${rankedCount} gesetzt`}
          </span>
        </div>
        {rankedTeachers.length === 0 ? (
          <p className="text-sm text-slate-500">Niemand ist in allen Slots dieses Stücks frei. Die Automatik sucht danach.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {rankedTeachers.map(({ teacher, ok }) => {
              const priority = sampleBlock?.entries.find(e => e.teacherId === teacher.id)
              return (
                <TeacherPriorityCard
                  key={`${seriesKey}-${teacher.id}`}
                  teacher={teacher}
                  priorityRank={ok ? (priority?.rank ?? 0) : 0}
                  onToggle={() => ok && onToggleTeacher(teacher, seriesKey)}
                  timeSlotsCount={slotIds.length}
                  selectedDate={focusDate}
                  requestMode="sequential"
                  backgroundName={sampleBlock?.blockName || liveUnit?.lessons[0]?.subjectName || undefined}
                  disabled={!ok}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
